/**
 * Relationships API
 * Entity dependency checks and relationship queries (check-delete, property-units, tenant-payments, etc.).
 */

import { apiTransport as router } from './transport';
import { getContext } from './core';

export interface RelationshipCheck {
  canDelete: boolean;
  dependencies: Array<{ type: string; count: number; ids?: string[] }>;
  error?: string;
}

export const relationshipsApi = {
  checkDelete: async (entityType: string, entityId: string): Promise<RelationshipCheck> => {
    const context = getContext();
    return router.post<RelationshipCheck>('/relationships/check-delete', { entityType, entityId }, context);
  },

  cascadeDelete: async (
    entityType: string,
    entityId: string
  ): Promise<{ deleted: string[]; errors: string[] }> => {
    const context = getContext();
    return router.post<{ deleted: string[]; errors: string[] }>(
      '/relationships/cascade-delete',
      { entityType, entityId },
      context
    );
  },

  related: async (entityType: string, entityId: string): Promise<Record<string, unknown[]>> => {
    const context = getContext();
    return router.post<Record<string, unknown[]>>(
      '/relationships/related',
      { entityType, entityId },
      context
    );
  },

  verifyIntegrity: async (
    entityType: string,
    entityId: string,
    foreignKey: string,
    foreignEntityType: string
  ): Promise<boolean> => {
    const context = getContext();
    return router.post<boolean>(
      '/relationships/verify-integrity',
      { entityType, entityId, foreignKey, foreignEntityType },
      context
    );
  },

  propertyUnits: async (propertyId: string): Promise<unknown[]> => {
    const context = getContext();
    return router.post<unknown[]>('/relationships/property-units', { propertyId }, context);
  },

  tenantPayments: async (tenantId: string): Promise<unknown[]> => {
    const context = getContext();
    return router.post<unknown[]>('/relationships/tenant-payments', { tenantId }, context);
  },

  tenantDocuments: async (tenantId: string): Promise<unknown[]> => {
    const context = getContext();
    return router.post<unknown[]>('/relationships/tenant-documents', { tenantId }, context);
  },

  propertyTenants: async (propertyId: string): Promise<unknown[]> => {
    const context = getContext();
    return router.post<unknown[]>('/relationships/property-tenants', { propertyId }, context);
  },
};
